import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * FD-614 — an index-route redirect that keeps the query string and survives the
 * `?env=` keeper.
 *
 * `<Navigate to="overview" replace />` broke a campaign entered from the list or
 * Home. Those links navigate to `/migrations/<id>` with no query string, and in
 * ONE commit two effects navigate: this redirect (child, first) to `…/overview`,
 * then EnvironmentProvider's `?env=` keeper (parent, last) to `?env=…` resolved
 * against the STALE pathname `/migrations/<id>`. The keeper's navigation wins.
 * `<Navigate>`'s effect is keyed on its props, which had not changed, so it never
 * fired again and the index route rendered nothing — a blank content column.
 *
 * Two differences from `<Navigate>`, and both matter:
 *
 * - The effect is keyed on the LOCATION, so if anything replaces the location
 *   while this index route is still matched, the redirect runs again from the new
 *   one. That is what makes it robust to the keeper rather than to one ordering.
 * - It carries `search` and `hash` across, so `?env=` (and anything else) is not
 *   dropped by the redirect itself.
 *
 * `to` is resolved relative to the route, exactly as `<Navigate to>` is.
 */
export default function RedirectPreservingSearch({ to }: { to: string }) {
  const navigate = useNavigate();
  const { search, hash, key } = useLocation();

  useEffect(() => {
    navigate({ pathname: to, search, hash }, { replace: true });
  }, [navigate, to, search, hash, key]);

  return null;
}
